import { normalizeCartDisplay, type CartDisplay } from "../presentation";
import { normalizeStorefrontFeatures, type StorefrontFeatures } from "../features";
import { normalizePublicProductConfigurator, type PublicProductConfigurator } from "./product-configurator-ui";
import { isSafePublicMediaUrl } from "./media-url";
import { safeCheckoutPath } from "./checkout-path";

export type UnknownRecord = Record<string, unknown>;

export type StorefrontBackorderPolicy = "deny" | "notify" | "allow";

export type StorefrontProductMedia = {
  id: string;
  mediaId?: number;
  variantId?: string;
  role: "image" | "gallery" | "download";
  altText?: string;
  mime?: string;
  poster?: string;
  position: number;
  src: string;
  srcsetWebp?: string;
  srcsetAvif?: string;
  width?: number;
  height?: number;
};

export type StorefrontProductAttribute = {
  name: string;
  canonicalName?: string;
  values: string[];
  valueLabels?: Record<string, string>;
  visible: boolean;
  usedForVariations: boolean;
};

export type StorefrontDimensions = {
  lengthMm?: number;
  widthMm?: number;
  heightMm?: number;
};

export type StorefrontTaxPrices = {
  regular: { inclusiveMinor: number; exclusiveMinor: number };
  sale?: { inclusiveMinor: number; exclusiveMinor: number };
};

export type StorefrontProduct = {
  readonly _normalizedByWebshopsClient: true;
  brands?: {id:string;name:string;slug:string}[];
  id: string;
  slug: string;
  name: string;
  description?: string;
  shortDescription?: string;
  /** Rich text sanitized by the public webshop service before it reaches the browser. */
  descriptionHtml?: string;
  /** Rich text sanitized by the public webshop service before it reaches the browser. */
  shortDescriptionHtml?: string;
  sku?: string;
  imageUrl?: string;
  media: StorefrontProductMedia[];
  attributes: StorefrontProductAttribute[];
  price?: number;
  regularPrice?: number;
  salePrice?: number;
  scheduledSalePrice?: number;
  saleStartsAt?: string;
  saleEndsAt?: string;
  onSale: boolean;
  currency?: string;
  pricesIncludeTax: boolean;
  taxPrices?: StorefrontTaxPrices;
  contactUrl?: string;
  stockQuantity?: number;
  inStock?: boolean;
  manageStock: boolean;
  stockStatus?: "in-stock" | "out-of-stock";
  backorderPolicy: StorefrontBackorderPolicy;
  allowBackorder: boolean;
  lowStockThreshold?: number;
  lowStock: boolean;
  soldIndividually: boolean;
  virtual: boolean;
  downloadable: boolean;
  weightGrams?: number;
  dimensions: StorefrontDimensions;
  featured?: boolean;
  status?: string;
  productType?: "simple" | "variable";
  url?: string;
  variantId?: string;
  configurator?: PublicProductConfigurator;
  requiresConfiguration?: boolean;
  variants: StorefrontVariant[];
  upsells: StorefrontProduct[];
  crossSells: StorefrontProduct[];
};

export type StorefrontVariant = {
  id: string;
  sku?: string;
  globalUniqueId?: string;
  globalUniqueIdType?: "gtin" | "upc" | "ean" | "isbn";
  title?: string;
  /** Localized labels for UI only; optionValues remains canonical for matching. */
  displayOptionValues?: Record<string, string>;
  optionValues: Record<string, string>;
  imageUrl?: string;
  price?: number;
  regularPrice?: number;
  salePrice?: number;
  scheduledSalePrice?: number;
  saleStartsAt?: string;
  saleEndsAt?: string;
  onSale: boolean;
  currency?: string;
  stockQuantity?: number;
  stockOnHand?: number;
  availableQuantity?: number;
  manageStock: boolean;
  stockStatus?: "in-stock" | "out-of-stock";
  inStock: boolean;
  backorderPolicy: StorefrontBackorderPolicy;
  allowBackorder: boolean;
  lowStockThreshold?: number;
  lowStock: boolean;
  taxable?: boolean;
  taxStatus?: "taxable" | "shipping-only" | "none";
  taxClassId?: string;
  taxPrices?: StorefrontTaxPrices;
  weightGrams?: number;
  dimensions: StorefrontDimensions;
  shippingClassId?: string;
  virtual: boolean;
  downloadable: boolean;
  downloadLimit?: number;
  downloadExpiryDays?: number;
};

export type StorefrontCategory = {
  id: string;
  parentId?: string | null;
  position?: number;
  slug: string;
  name: string;
  description?: string;
  imageUrl?: string;
  media?: StorefrontProductMedia;
  displayMode?: "products" | "subcategories" | "both";
  productCount?: number;
  url?: string;
};

export type CartLine = {
  id: string;
  productId?: string;
  variantId?: string;
  name: string;
  /** Immutable localized labels returned by the cart locale. */
  optionValues?: Record<string, string>;
  configuration?: {
    fields: Array<{ key: string; label: string; displayValue: string; unit?: string }>;
    artworks: Array<{ surfaceLabel: string; templateLabel: string }>;
  };
  imageUrl?: string;
  quantity: number;
  soldIndividually: boolean;
  downloadable: boolean;
  stockQuantity?: number;
  allowBackorder: boolean;
  unitPrice?: number;
  lineTotal?: number;
  currency?: string;
  url?: string;
};

export type StorefrontCart = {
  currencyMinorDigits?: number;
  paymentMethods?: Array<"mollie"|"paynl"|"paypal"|"buckaroo"|"bank_transfer"|"cash_on_delivery">;
  shippingDestination?: { countryCode: string; postalCode: string; city: string; state: string };
  advancedShipping?: {
    enabled: true; available: boolean; selectionRequired: boolean; selectedMethodId?: string;
    options: Array<{ methodId: string; title: string; subtitle?: string; description?: string; amountMinor: number; taxMinor: number; messages: string[] }>;
    messages: string[]; automaticCouponCode?: string; automaticCouponLabel?: string; currencies: string[];
  };
  features?: StorefrontFeatures;
  display?: CartDisplay;
  id?: string;
  token?: string;
  currency: string;
  items: CartLine[];
  itemCount: number;
  subtotal?: number;
  discountTotal?: number;
  shippingTotal?: number;
  taxTotal?: number;
  total?: number;
  checkoutUrl?: string;
  quoteVersion?: string;
  couponCode?: string;
  couponInvalid: boolean;
  invalidCouponCode?: string;
  crossSells: StorefrontProduct[];
};

export function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function readString(record: UnknownRecord, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return undefined;
}

export function readNumber(record: UnknownRecord, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim()) {
      const parsed = Number(value.replace(",", "."));
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return undefined;
}

export function readBoolean(record: UnknownRecord, ...keys: string[]): boolean | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "boolean") return value;
    if (value === 1 || value === "1" || value === "true") return true;
    if (value === 0 || value === "0" || value === "false") return false;
  }
  return undefined;
}

export function readArray(value: unknown, ...keys: string[]): unknown[] {
  if (Array.isArray(value)) return value;
  if (!isRecord(value)) return [];

  for (const key of keys) {
    const candidate = value[key];
    if (Array.isArray(candidate)) return candidate;
  }

  if (isRecord(value.data)) return readArray(value.data, ...keys);
  return [];
}

function nestedRecord(record: UnknownRecord, key: string): UnknownRecord | undefined {
  const value = record[key];
  return isRecord(value) ? value : undefined;
}

function uploadUrl(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  return isSafePublicMediaUrl(normalized) ? normalized : undefined;
}

function readUploadUrl(record: UnknownRecord, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = uploadUrl(record[key]);
    if (value) return value;
  }
  return undefined;
}

function readImageUrl(record: UnknownRecord): string | undefined {
  const direct = readUploadUrl(record, "imageUrl", "image", "thumbnailUrl", "thumbnail", "src");
  if (direct) return direct;

  const image = nestedRecord(record, "featuredImage") ?? nestedRecord(record, "image");
  return image ? readUploadUrl(image, "url", "src") : undefined;
}

function readMoneyMinor(record: UnknownRecord, minorKeys: string[], majorKeys: string[]): number | undefined {
  const minor = readNumber(record, ...minorKeys);
  if (minor !== undefined) return minor;
  const major = readNumber(record, ...majorKeys);
  return major === undefined ? undefined : Math.round(major * 100);
}

function readDate(record: UnknownRecord, ...keys: string[]): string | undefined {
  const value = readString(record, ...keys);
  if (!value || !Number.isFinite(Date.parse(value))) return undefined;
  return value;
}

function richTextToPlainText(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const text = value
    .replace(/<\s*br\s*\/?>/giu, "\n")
    .replace(/<\/(?:p|div|li|h[1-6])\s*>/giu, "\n")
    .replace(/<[^>]*>/gu, " ")
    .replace(/&nbsp;/giu, " ")
    .replace(/&amp;/giu, "&")
    .replace(/&lt;/giu, "<")
    .replace(/&gt;/giu, ">")
    .replace(/&quot;/giu, '"')
    .replace(/&#(?:39|x27);/giu, "'")
    .replace(/[ \t]+/gu, " ")
    .replace(/\s*\n\s*/gu, "\n")
    .trim();
  return text || undefined;
}

function normalizeBackorderPolicy(record: UnknownRecord): StorefrontBackorderPolicy {
  const value = readString(record, "backorderPolicy", "backorders", "backorder_policy")?.toLowerCase();
  if (value === "notify" || value === "allow-notify" || value === "allow_notify") return "notify";
  if (value === "allow" || value === "yes" || value === "true") return "allow";
  if (value === "deny" || value === "no" || value === "false") return "deny";
  return readBoolean(record, "allowBackorder", "backordersAllowed") ? "allow" : "deny";
}

function normalizeStockStatus(record: UnknownRecord): "in-stock" | "out-of-stock" | undefined {
  const value = readString(record, "stockStatus", "stock_status")?.toLowerCase();
  if (value === "out-of-stock" || value === "outofstock") return "out-of-stock";
  if (value === "in-stock" || value === "instock") return "in-stock";
  return undefined;
}

function normalizeDimensions(record: UnknownRecord): StorefrontDimensions {
  const dimensions = nestedRecord(record, "dimensions") ?? {};
  return {
    lengthMm:
      readNumber(record, "lengthMm", "lengthMillimeters") ?? readNumber(dimensions, "lengthMm", "lengthMillimeters"),
    widthMm:
      readNumber(record, "widthMm", "widthMillimeters") ?? readNumber(dimensions, "widthMm", "widthMillimeters"),
    heightMm:
      readNumber(record, "heightMm", "heightMillimeters") ?? readNumber(dimensions, "heightMm", "heightMillimeters"),
  };
}

function normalizeWeightGrams(record: UnknownRecord): number | undefined {
  const grams = readNumber(record, "weightGrams", "weight_g");
  if (grams !== undefined) return grams;
  const kilograms = readNumber(record, "weightKg", "weightKilograms");
  return kilograms === undefined ? undefined : Math.round(kilograms * 1_000);
}

function normalizeMedia(product: UnknownRecord, productName: string): StorefrontProductMedia[] {
  const media = readArray(product, "media", "gallery", "images").flatMap((value, index): StorefrontProductMedia[] => {
    if (!isRecord(value)) return [];
    const roleValue = readString(value, "role", "type")?.toLowerCase();
    if (roleValue === "download") return [];
    const src = readUploadUrl(value, "src", "url", "imageUrl");
    if (!src) return [];
    const role = roleValue === "image" ? "image" : "gallery";
    const mediaId = readNumber(value, "mediaId", "uploadId");
    const variantId = readString(value, "variantId");
    return [{
      id: readString(value, "id") ?? `${variantId ?? "product"}-${mediaId ?? index}`,
      mediaId,
      variantId,
      role,
      altText: readString(value, "altText", "alt") ?? productName,
      mime: readString(value, "mime"),
      poster: readUploadUrl(value, "poster"),
      position: Math.trunc(readNumber(value, "position", "sortOrder") ?? index),
      src,
      srcsetWebp: safeMediaSrcset(value.srcsetWebp),
      srcsetAvif: safeMediaSrcset(value.srcsetAvif),
      width: positiveMediaDimension(value.width),
      height: positiveMediaDimension(value.height),
    }];
  });

  const fallback = readImageUrl(product);
  if (fallback && !media.some((item) => item.src === fallback)) {
    media.push({ id: "featured", role: "image", altText: productName, position: -1, src: fallback });
  }

  return media.sort((left, right) => left.position - right.position || left.id.localeCompare(right.id));
}

function positiveMediaDimension(value: unknown): number | undefined {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : undefined;
}

export function safeMediaSrcset(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length > 20_000) return undefined;
  const entries = value.split(",").map((entry) => entry.trim());
  if (!entries.length || entries.some((entry) => {
    const parts = entry.split(/\s+/);
    return parts.length !== 2 || !isSafePublicMediaUrl(parts[0]) || !/^[1-9]\d{0,4}w$/.test(parts[1]);
  })) return undefined;
  return entries.join(", ");
}

type NormalizedPricing = Pick<
  StorefrontVariant,
  "price" | "regularPrice" | "salePrice" | "scheduledSalePrice" | "saleStartsAt" | "saleEndsAt" | "onSale"
>;

function normalizePricing(record: UnknownRecord): NormalizedPricing {
  if (readBoolean(record, "priceOnRequest", "price_on_request")) return { price: undefined, regularPrice: undefined, salePrice: undefined, scheduledSalePrice: undefined, saleStartsAt: undefined, saleEndsAt: undefined, onSale: false };
  const regularPrice = readMoneyMinor(record, ["regularPriceMinor"], ["regularPrice", "compareAtPrice"]);
  const responseSalePrice = readMoneyMinor(record, ["salePriceMinor"], ["salePrice"]);
  const scheduledSalePrice =
    readMoneyMinor(record, ["scheduledSalePriceMinor"], ["scheduledSalePrice"]) ?? responseSalePrice;
  const saleStartsAt = readDate(record, "saleStartsAt", "salePriceStartsAt", "dateOnSaleFrom", "sale_starts_at");
  const saleEndsAt = readDate(record, "saleEndsAt", "salePriceEndsAt", "dateOnSaleTo", "sale_ends_at");
  const now = Date.now();
  const withinWindow =
    (!saleStartsAt || Date.parse(saleStartsAt) <= now) && (!saleEndsAt || Date.parse(saleEndsAt) > now);
  const explicitlyOnSale = readBoolean(record, "onSale", "isOnSale");
  const saleCandidate = responseSalePrice ?? scheduledSalePrice;
  const activeByContract = explicitlyOnSale ?? (saleCandidate !== undefined && withinWindow);
  const activeSalePrice = activeByContract ? saleCandidate : undefined;
  const responsePrice = readMoneyMinor(record, ["priceMinor", "effectivePriceMinor"], ["price", "currentPrice", "amount"]);
  const price = activeByContract
    ? responsePrice ?? activeSalePrice ?? regularPrice
    : regularPrice ?? responsePrice;
  const onSale = Boolean(
    activeByContract &&
      activeSalePrice !== undefined &&
      (regularPrice === undefined || activeSalePrice < regularPrice),
  );

  return {
    price,
    regularPrice,
    salePrice: onSale ? activeSalePrice : undefined,
    scheduledSalePrice,
    saleStartsAt,
    saleEndsAt,
    onSale,
  };
}

function normalizeTaxPrices(value: unknown): StorefrontTaxPrices | undefined {
  if (!isRecord(value)) return undefined;
  const amount = (pair: unknown) => {
    if (!isRecord(pair)) return undefined;
    const inclusiveMinor = readNumber(pair, "inclusiveMinor"), exclusiveMinor = readNumber(pair, "exclusiveMinor");
    return inclusiveMinor !== undefined && exclusiveMinor !== undefined && Number.isSafeInteger(inclusiveMinor) && Number.isSafeInteger(exclusiveMinor) && inclusiveMinor >= 0 && exclusiveMinor >= 0
      ? { inclusiveMinor, exclusiveMinor } : undefined;
  };
  const regular = amount(value.regular);
  return regular ? { regular, sale: amount(value.sale) } : undefined;
}

function normalizeVariant(value: unknown, media: StorefrontProductMedia[]): StorefrontVariant | null {
  if (!isRecord(value)) return null;
  const id = readString(value, "id", "variantId");
  if (!id) return null;

  const optionSource = nestedRecord(value, "optionValues") ?? nestedRecord(value, "options") ?? {};
  const optionValues = Object.fromEntries(
    Object.entries(optionSource).flatMap(([name, optionValue]): Array<[string, string]> => {
      if (typeof optionValue !== "string" || !optionValue.trim()) return [];
      return [[name, optionValue.trim()]];
    }),
  );
  const displayOptionSource = nestedRecord(value, "displayOptionValues") ?? {};
  const displayOptionValues = Object.fromEntries(
    Object.entries(displayOptionSource).flatMap(([name, optionValue]): Array<[string, string]> =>
      typeof optionValue === "string" && optionValue.trim() ? [[name, optionValue.trim()]] : [],
    ),
  );
  const manageStock = readBoolean(value, "manageStock", "trackInventory") ?? false;
  const stockOnHand = readNumber(value, "stockOnHand", "inventory");
  const reservedQuantity = readNumber(value, "reservedQuantity") ?? 0;
  const availableQuantity =
    readNumber(value, "availableQuantity", "stockQuantity", "stock") ??
    (manageStock && stockOnHand !== undefined ? Math.max(0, stockOnHand - reservedQuantity) : undefined);
  const stockStatus = normalizeStockStatus(value);
  const backorderPolicy = normalizeBackorderPolicy(value);
  const allowBackorder = backorderPolicy !== "deny";
  const explicitInStock = readBoolean(value, "inStock", "available", "isAvailable");
  const inStock =
    explicitInStock ??
    (stockStatus !== "out-of-stock" && (!manageStock || availableQuantity === undefined || availableQuantity > 0 || allowBackorder));
  const lowStockThreshold = readNumber(value, "lowStockThreshold");
  const globalUniqueIdTypeValue = readString(value, "globalUniqueIdType", "identifierType")?.toLowerCase();
  const globalUniqueIdType =
    globalUniqueIdTypeValue === "gtin" ||
    globalUniqueIdTypeValue === "upc" ||
    globalUniqueIdTypeValue === "ean" ||
    globalUniqueIdTypeValue === "isbn"
      ? globalUniqueIdTypeValue
      : undefined;
  const taxStatusValue = readString(value, "taxStatus")?.toLowerCase();
  const taxStatus =
    taxStatusValue === "taxable" || taxStatusValue === "shipping-only" || taxStatusValue === "none"
      ? taxStatusValue
      : undefined;
  const pricing = normalizePricing(value);

  return {
    id,
    sku: readString(value, "sku"),
    globalUniqueId: readString(value, "globalUniqueId", "gtin", "ean", "upc", "isbn"),
    globalUniqueIdType,
    title: readString(value, "title", "name"),
    optionValues,
    ...(Object.keys(displayOptionValues).length ? { displayOptionValues } : {}),
    imageUrl:
      media.find((item) => item.variantId === id && item.role === "image")?.src ??
      media.find((item) => item.variantId === id && item.role === "gallery")?.src ?? readImageUrl(value),
    ...pricing,
    currency: readString(value, "currency", "currencyCode"),
    stockQuantity: availableQuantity,
    stockOnHand,
    availableQuantity,
    manageStock,
    stockStatus,
    inStock,
    backorderPolicy,
    allowBackorder,
    lowStockThreshold,
    lowStock: Boolean(manageStock && lowStockThreshold !== undefined && availableQuantity !== undefined && availableQuantity <= lowStockThreshold),
    taxable: readBoolean(value, "taxable"),
    taxStatus,
    taxClassId: readString(value, "taxClassId"),
    taxPrices: normalizeTaxPrices(value.taxPrices),
    weightGrams: normalizeWeightGrams(value),
    dimensions: normalizeDimensions(value),
    shippingClassId: readString(value, "shippingClassId"),
    virtual: readBoolean(value, "virtual", "isVirtual") ?? false,
    downloadable: readBoolean(value, "downloadable", "isDownloadable") ?? false,
    downloadLimit: readNumber(value, "downloadLimit"),
    downloadExpiryDays: readNumber(value, "downloadExpiryDays"),
  };
}

function normalizeAttributes(product: UnknownRecord, variants: StorefrontVariant[]): StorefrontProductAttribute[] {
  const normalized: StorefrontProductAttribute[] = [];
  const rawAttributes = product.attributes;

  if (Array.isArray(rawAttributes)) {
    for (const value of rawAttributes) {
      if (!isRecord(value)) continue;
      const name = readString(value, "name", "label");
      if (!name) continue;
      const canonicalName = readString(value, "canonicalName") ?? name;
      const values = readArray(value, "values", "options")
        .flatMap((option): string[] => (typeof option === "string" && option.trim() ? [option.trim()] : []));
      normalized.push({
        name,
        ...(canonicalName !== name ? { canonicalName } : {}),
        values: [...new Set(values)],
        ...(isRecord(value.valueLabels)
          ? {
              valueLabels: Object.fromEntries(Object.entries(value.valueLabels).flatMap(([canonical, label]) =>
                typeof label === "string" && label.trim() ? [[canonical, label.trim()] as const] : [],
              )),
            }
          : {}),
        visible: readBoolean(value, "visible") ?? true,
        usedForVariations: readBoolean(value, "usedForVariations", "variation") ?? false,
      });
    }
  } else if (isRecord(rawAttributes)) {
    for (const [name, value] of Object.entries(rawAttributes)) {
      const values = (Array.isArray(value) ? value : [value])
        .flatMap((option): string[] => (typeof option === "string" && option.trim() ? [option.trim()] : []));
      if (name.trim() && values.length) {
        normalized.push({ name: name.trim(), values: [...new Set(values)], visible: true, usedForVariations: true });
      }
    }
  }

  for (const variant of variants) {
    for (const [name, value] of Object.entries(variant.optionValues)) {
      const attribute = normalized.find((item) => (item.canonicalName ?? item.name) === name);
      if (attribute) {
        if (!attribute.values.includes(value)) attribute.values.push(value);
        attribute.usedForVariations = true;
      } else {
        normalized.push({ name, canonicalName: name, values: [value], visible: true, usedForVariations: true });
      }
    }
  }

  return normalized;
}

export function normalizeProduct(value: unknown): StorefrontProduct | null {
  if (!isRecord(value)) return null;
  const product = nestedRecord(value, "product") ?? value;
  if (product._normalizedByWebshopsClient === true) return product as StorefrontProduct;
  const id = readString(product, "id", "productId", "uuid");
  const name = readString(product, "name", "title");
  if (!id || !name) return null;

  const slug = readString(product, "slug") ?? id;
  const media = normalizeMedia(product, name);
  const variants = readArray(product, "variants")
    .map((variant) => normalizeVariant(variant, media))
    .filter((variant): variant is StorefrontVariant => variant !== null);
  const requestedDefaultVariantId = readString(product, "defaultVariantId", "variantId");
  const defaultVariant =
    variants.find((variant) => variant.id === requestedDefaultVariantId) ??
    variants.find((variant) => variant.inStock) ??
    variants[0];
  const priceRange = nestedRecord(product, "priceRange");
  const stockQuantity =
    readNumber(product, "availableQuantity", "stockQuantity", "stock", "inventory") ?? defaultVariant?.stockQuantity;
  const explicitInStock = readBoolean(product, "inStock", "available", "isAvailable");
  const priceObject = nestedRecord(product, "price");
  const productPricing = normalizePricing(product);
  const pricing = defaultVariant
    ? {
        price: defaultVariant.price,
        regularPrice: defaultVariant.regularPrice,
        salePrice: defaultVariant.salePrice,
        scheduledSalePrice: defaultVariant.scheduledSalePrice,
        saleStartsAt: defaultVariant.saleStartsAt,
        saleEndsAt: defaultVariant.saleEndsAt,
        onSale: defaultVariant.onSale,
      }
    : productPricing;
  const fallbackRangePrice = priceRange ? readNumber(priceRange, "minMinor", "minimumMinor", "min") : undefined;
  const shortDescriptionHtml = readString(product, "shortDescriptionHtml", "sanitizedShortDescriptionHtml", "shortDescription");
  const descriptionHtml = readString(product, "descriptionHtml", "sanitizedDescriptionHtml", "description");
  const manageStock = defaultVariant?.manageStock ?? (readBoolean(product, "manageStock", "trackInventory") ?? false);
  const backorderPolicy = defaultVariant?.backorderPolicy ?? normalizeBackorderPolicy(product);
  const lowStockThreshold = defaultVariant?.lowStockThreshold ?? readNumber(product, "lowStockThreshold");
  const dimensions = defaultVariant?.dimensions ?? normalizeDimensions(product);

  return {
    _normalizedByWebshopsClient: true,
    id,
    slug,
    name,
    description: readString(product, "descriptionText") ?? richTextToPlainText(descriptionHtml),
    shortDescription:
      readString(product, "shortDescriptionText", "summary", "excerpt") ?? richTextToPlainText(shortDescriptionHtml),
    descriptionHtml,
    shortDescriptionHtml,
    sku: readString(product, "sku"),
    imageUrl:
      defaultVariant?.imageUrl ??
      media.find((item) => !item.variantId && item.role === "image")?.src ??
      media.find((item) => !item.variantId && item.role === "gallery")?.src ??
      media.find((item) => item.role === "image")?.src ??
      media[0]?.src,
    media,
    brands: readArray(product,"brands").filter(isRecord).flatMap(b=>{
      const id=readString(b,"id"),name=readString(b,"name"),slug=readString(b,"slug");return id&&name&&slug?[{id,name,slug}]:[];
    }),
    attributes: normalizeAttributes(product, variants),
    requiresConfiguration: readBoolean(product, "requiresConfiguration") ?? Boolean(product.configurator),
    price: defaultVariant && defaultVariant.price === undefined ? undefined : pricing.price ?? fallbackRangePrice ??
      (priceObject ? readMoneyMinor(priceObject, ["amountMinor"], ["amount", "value"]) : undefined) ??
      readMoneyMinor(product, [], ["price", "currentPrice", "amount"]),
    regularPrice: pricing.regularPrice,
    salePrice: pricing.salePrice,
    scheduledSalePrice: pricing.scheduledSalePrice,
    saleStartsAt: pricing.saleStartsAt,
    saleEndsAt: pricing.saleEndsAt,
    onSale: pricing.onSale,
    currency:
      readString(product, "currency", "currencyCode") ??
      (priceObject ? readString(priceObject, "currency", "currencyCode") : undefined) ??
      (priceRange ? readString(priceRange, "currency", "currencyCode") : undefined) ??
      defaultVariant?.currency,
    pricesIncludeTax: readBoolean(product, "pricesIncludeTax", "prices_include_tax") ?? true,
    taxPrices: defaultVariant?.taxPrices ?? normalizeTaxPrices(product.taxPrices),
    contactUrl: readString(product, "contactUrl"),
    stockQuantity,
    inStock:
      explicitInStock ??
      (variants.length ? variants.some((variant) => variant.inStock) : stockQuantity === undefined || stockQuantity > 0 || backorderPolicy !== "deny"),
    manageStock,
    stockStatus: defaultVariant?.stockStatus ?? normalizeStockStatus(product),
    backorderPolicy,
    allowBackorder: backorderPolicy !== "deny",
    lowStockThreshold,
    lowStock:
      defaultVariant?.lowStock ??
      Boolean(manageStock && lowStockThreshold !== undefined && stockQuantity !== undefined && stockQuantity <= lowStockThreshold),
    soldIndividually: readBoolean(product, "soldIndividually", "individuallySold") ?? false,
    virtual: defaultVariant?.virtual ?? (readBoolean(product, "virtual", "isVirtual") ?? false),
    downloadable: defaultVariant?.downloadable ?? (readBoolean(product, "downloadable", "isDownloadable") ?? false),
    weightGrams: defaultVariant?.weightGrams ?? normalizeWeightGrams(product),
    dimensions,
    featured: readBoolean(product, "featured", "isFeatured"),
    status: readString(product, "status"),
    productType: readString(product, "productType") === "variable" || variants.length > 1 ? "variable" : "simple",
    url: readString(product, "url", "href"),
    variantId: requestedDefaultVariantId ?? defaultVariant?.id,
    configurator: normalizePublicProductConfigurator(product.configurator) ?? undefined,
    variants,
    upsells: normalizeProducts(product.upsells),
    crossSells: normalizeProducts(product.crossSells),
  };
}

export function normalizeProducts(value: unknown): StorefrontProduct[] {
  return readArray(value, "products", "items", "results")
    .map(normalizeProduct)
    .filter((item): item is StorefrontProduct => item !== null);
}

export function normalizeCategory(value: unknown): StorefrontCategory | null {
  if (!isRecord(value)) return null;
  const id = readString(value, "id", "categoryId", "uuid");
  const name = readString(value, "name", "title");
  if (!id || !name) return null;
  const parentId = readString(value, "parentId", "parent_id");
  const position = readNumber(value, "position", "menuOrder");
  const media = isRecord(value.media) ? normalizeMedia({ media: [value.media] }, name)[0] : undefined;
  const displayMode = value.displayMode === "products" || value.displayMode === "subcategories" || value.displayMode === "both" ? value.displayMode : undefined;

  return {
    id,
    ...(("parentId" in value || "parent_id" in value) ? { parentId: parentId ?? null } : {}),
    ...(position !== undefined ? { position } : {}),
    slug: readString(value, "slug") ?? id,
    name,
    description: readString(value, "description", "summary"),
    imageUrl: readImageUrl(value),
    ...(media ? { media } : {}),
    ...(displayMode ? { displayMode } : {}),
    productCount: readNumber(value, "productCount", "count", "productsCount"),
    url: readString(value, "url", "href"),
  };
}

export function normalizeCategories(value: unknown): StorefrontCategory[] {
  return readArray(value, "categories", "items", "results")
    .map(normalizeCategory)
    .filter((item): item is StorefrontCategory => item !== null);
}

export function normalizeCart(value: unknown): StorefrontCart {
  const root = isRecord(value) ? value : {};
  const data = isRecord(root.data) ? root.data : root;
  const cart = isRecord(data.cart) ? data.cart : data;
  const quote = isRecord(data.quote)
    ? data.quote
    : isRecord(root.quote)
      ? root.quote
      : isRecord(cart.quote)
        ? cart.quote
        : {};
  const currency = readString(cart, "currency", "currencyCode") ?? readString(quote, "currency", "currencyCode") ?? "EUR";
  const rawDigits = readNumber(cart, "currencyMinorDigits") ?? readNumber(quote, "currencyMinorDigits");
  const minorDigits = rawDigits !== undefined && Number.isInteger(rawDigits) && rawDigits >= 0 && rawDigits <= 6 ? rawDigits : 2;
  const shippingSource = nestedRecord(cart, "advancedShipping") ?? nestedRecord(quote, "advancedShipping");
  const destination = nestedRecord(cart, "shippingDestination");
  const shipping = shippingSource?.enabled === true ? {
    enabled: true as const, available: shippingSource.available === true, selectionRequired: shippingSource.selectionRequired === true,
    selectedMethodId: readString(shippingSource, "selectedMethodId"),
    options: readArray(shippingSource, "options").flatMap((option) => {
      if (!isRecord(option)) return [];
      const methodId = readString(option, "methodId"), title = readString(option, "title"), amountMinor = readNumber(option, "amountMinor"), taxMinor = readNumber(option, "taxMinor");
      if (!methodId || !title || amountMinor === undefined || !Number.isSafeInteger(amountMinor) || amountMinor < 0 || taxMinor === undefined || !Number.isSafeInteger(taxMinor) || taxMinor < 0) return [];
      return [{ methodId,title,amountMinor,taxMinor,subtitle:readString(option,"subtitle"),description:readString(option,"description"),messages:readArray(option,"messages").filter((message): message is string => typeof message === "string") }];
    }),
    messages: readArray(shippingSource,"messages").filter((message): message is string => typeof message === "string"),
    automaticCouponCode: readString(shippingSource,"automaticCouponCode"), automaticCouponLabel: readString(shippingSource,"automaticCouponLabel"),
    currencies: readArray(shippingSource,"currencies").filter((value): value is string => typeof value === "string" && /^[A-Z]{3}$/.test(value)),
  } : undefined;
  const rawLines = readArray(cart, "items", "lines", "cartItems");
  const rawQuoteVersion = readString(cart, "quoteVersion") ?? readString(quote, "quoteVersion");

  const items = rawLines.flatMap((raw): CartLine[] => {
    if (!isRecord(raw)) return [];
    const product = nestedRecord(raw, "product") ?? {};
    const variant = nestedRecord(raw, "variant") ?? {};
    const quotedLine = readArray(quote, "lines").find(
      (line) => isRecord(line) && readString(line, "lineId", "id") === readString(raw, "id", "itemId", "lineId"),
    );
    const lineQuote = isRecord(quotedLine) ? quotedLine : {};
    const id = readString(raw, "id", "itemId", "lineId");
    const productId = readString(raw, "productId") ?? readString(product, "id", "productId");
    const name = readString(raw, "name", "productName", "title") ?? readString(product, "name", "title");
    if (!id || !name) return [];

    const configurationSource = nestedRecord(raw, "configuration");
    const localizedConfiguration = configurationSource ? {
      fields: readArray(configurationSource, "fields").flatMap((field) => {
        if (!isRecord(field)) return [];
        const key = readString(field, "key");
        const label = readString(field, "label");
        const rawDisplayValue = field.displayValue;
        if (!key || !label || !["string", "number", "boolean"].includes(typeof rawDisplayValue)) return [];
        return [{ key, label, displayValue: String(rawDisplayValue), unit: readString(field, "unit") }];
      }),
      artworks: readArray(configurationSource, "artworks").flatMap((artwork) => {
        if (!isRecord(artwork)) return [];
        const surfaceLabel = readString(artwork, "surfaceLabel");
        const templateLabel = readString(artwork, "templateLabel");
        return surfaceLabel && templateLabel ? [{ surfaceLabel, templateLabel }] : [];
      }),
    } : undefined;

    return [
      {
        id,
        productId,
        variantId: readString(raw, "variantId"),
        name,
        optionValues: (() => {
          const values = nestedRecord(raw, "optionValues") ?? nestedRecord(variant, "displayOptionValues") ?? {};
          return Object.fromEntries(Object.entries(values).flatMap(([label, option]): Array<[string, string]> =>
            typeof option === "string" && option.trim() ? [[label, option.trim()]] : [],
          ));
        })(),
        ...(localizedConfiguration ? { configuration: localizedConfiguration } : {}),
        imageUrl: readImageUrl(raw) ?? readImageUrl(product),
        quantity: Math.max(1, Math.trunc(readNumber(raw, "quantity", "qty") ?? 1)),
        soldIndividually:
          readBoolean(raw, "soldIndividually") ?? readBoolean(product, "soldIndividually") ?? false,
        downloadable:
          readBoolean(raw, "downloadable") ?? readBoolean(product, "downloadable") ?? readBoolean(variant, "downloadable") ?? false,
        stockQuantity: readNumber(variant, "availableQuantity", "stockQuantity"),
        allowBackorder: readBoolean(variant, "allowBackorder") ?? false,
        unitPrice:
          readNumber(raw, "unitPriceMinor", "unitPrice", "priceMinor", "price") ??
          readNumber(variant, "priceMinor", "salePriceMinor", "regularPriceMinor"),
        lineTotal:
          readNumber(raw, "lineTotalMinor", "lineTotal", "totalMinor", "total", "subtotalMinor", "subtotal") ??
          readNumber(lineQuote, "totalMinor", "subtotalMinor"),
        currency: readString(raw, "currency", "currencyCode") ?? readString(variant, "currency", "currencyCode") ?? currency,
        url: readString(raw, "url", "href") ?? readString(product, "url", "href"),
      },
    ];
  });
  const currentProductIds = new Set(items.flatMap((item) => item.productId ? [item.productId] : []));
  const seenCrossSells = new Set<string>();
  const crossSellSource = cart.crossSells ?? data.crossSells ?? root.crossSells;
  const crossSells = normalizeProducts(crossSellSource)
    .filter((product) => {
      if (currentProductIds.has(product.id) || seenCrossSells.has(product.id)) return false;
      seenCrossSells.add(product.id);
      return true;
    })
    .slice(0, 8);

  return {
    ...(isRecord(data.display) ? { display: normalizeCartDisplay(data.display) } : {}),
    ...(isRecord(data.features) ? { features: normalizeStorefrontFeatures(data.features) } : {}),
    id: readString(cart, "id", "cartId"),
    token: readString(root, "token", "cartToken") ?? readString(data, "token", "cartToken") ?? readString(cart, "token"),
    currency,
    currencyMinorDigits: minorDigits,
    ...(Array.isArray(cart.paymentMethods) ? {paymentMethods:cart.paymentMethods.filter((value): value is NonNullable<StorefrontCart["paymentMethods"]>[number] => typeof value === "string" && ["mollie","paynl","paypal","buckaroo","bank_transfer","cash_on_delivery"].includes(value))} : {}),
    ...(shipping ? {advancedShipping: shipping} : {}),
    ...(destination ? {shippingDestination: {countryCode: readString(destination,"countryCode") ?? "",postalCode:readString(destination,"postalCode") ?? "",city:readString(destination,"city") ?? "",state:readString(destination,"state") ?? ""}} : {}),
    items,
    itemCount: Math.max(
      0,
      Math.trunc(readNumber(cart, "itemCount", "count") ?? items.reduce((total, item) => total + item.quantity, 0)),
    ),
    subtotal: readNumber(quote, "subtotalMinor", "subtotal") ?? readNumber(cart, "subtotalMinor", "subtotal"),
    discountTotal:
      readNumber(quote, "discountMinor", "discountTotal", "discount") ?? readNumber(cart, "discountMinor", "discountTotal"),
    shippingTotal:
      readNumber(quote, "shippingMinor", "shippingTotal", "shipping") ?? readNumber(cart, "shippingMinor", "shippingTotal"),
    taxTotal: readNumber(quote, "taxMinor", "taxTotal", "tax") ?? readNumber(cart, "taxMinor", "taxTotal"),
    total: readNumber(quote, "totalMinor", "total", "grandTotal") ?? readNumber(cart, "totalMinor", "total", "grandTotal"),
    checkoutUrl: safeCheckoutPath(readString(cart, "checkoutUrl")) ?? safeCheckoutPath(readString(data, "checkoutUrl")),
    quoteVersion: rawQuoteVersion && /^[a-f0-9]{64}$/.test(rawQuoteVersion) ? rawQuoteVersion : undefined,
    couponCode: readString(cart, "couponCode"),
    couponInvalid: readBoolean(cart, "couponInvalid") ?? false,
    invalidCouponCode: readString(cart, "invalidCouponCode"),
    crossSells,
  };
}

export function formatMoney(value: number | undefined, currency = "EUR", locale?: string, minorDigits = 2): string {
  if (value === undefined || !Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: minorDigits,
      maximumFractionDigits: minorDigits,
    }).format(value / 10 ** minorDigits);
  } catch {
    return `${currency.toUpperCase()} ${(value / 10 ** minorDigits).toFixed(minorDigits)}`;
  }
}

export function safePathSegment(value: string): string {
  return encodeURIComponent(value.replace(/^\/+|\/+$/g, ""));
}

/** The server is authoritative for guest-order cancellation eligibility. */
export function canCancelGuestOrder(value: unknown): boolean {
  return isRecord(value) && readBoolean(value, "canCancel") === true;
}
